import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule, formatDate } from '@angular/common';

import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { ButtonModule } from 'primeng/button';

import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';

import { Course } from '../../../models/course.model';
import { user } from '../../../models/user.model';

import { FiledropComponent } from '../../filedrop/filedrop.component';
import { Consultation } from '../../../models/consultation.model';
import { ConsultationService } from '../../../services/consultation.service';
import { ActivityService } from '../../../services/activity.service';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { UserService } from '../../../services/user.service';
import { DialogManagerService } from '../../../services/dialog-manager.service';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Activity } from '../../../models/activity.model';
import { CourseService } from '../../../services/course.service';
import { STATUS, STATUS_OPTIONS } from '../../../../constants';
import { CheckboxModule } from 'primeng/checkbox';
import { ListboxModule } from 'primeng/listbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { firstValueFrom, filter } from 'rxjs';
import { DialogResult } from 'src/app/workflow/models/dialog-result.model';

@Component({
  selector: 'app-create-consultation-dialog',
  standalone: true,
  imports: [
    DropdownModule,
    DialogModule,
    EditorModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CommonModule,
    FiledropComponent,
    FloatLabelModule,
    InputTextModule,
    CheckboxModule,
    ListboxModule,
    MultiSelectModule,
  ],
  templateUrl: './create-consultation-dialog.component.html',
  styleUrl: './create-consultation-dialog.component.scss',
})
export class CreateConsultationDialogComponent implements OnInit {
  @ViewChild(FiledropComponent) fileDropComponent!: FiledropComponent;

  selectedUser: any = null;
  activityToUpgrade: Activity = null;
  oldFiles: string[] = [];

  allParticipants: user[] = [];

  // Optionsliste des Teilnehmer-Multiselects; wird aus dem gewählten Kurs befüllt
  availableParticipants: user[] = [];
  availableCourses: Course[] = [];

  isSubmitting: boolean = false;
  usersLoading: boolean = true;
  coursesLoading: boolean = true;

  status = STATUS;
  statusOptions = STATUS_OPTIONS;

  consultationForm: FormGroup;

  disableParticipantSelect = true;

  private isDialogOpen = false;
  constructor(
    private authService: AuthService,
    private consultationService: ConsultationService,
    private notificationService: NotificationService,
    private activityService: ActivityService,
    private userService: UserService,
    private courseService: CourseService,
    private fb: FormBuilder,
    private dialogManager: DialogManagerService,
    private dialogConfig: DynamicDialogConfig,
  ) {}

  async ngOnInit(): Promise<void> {
    this.selectedUser = this.dialogConfig.data?.selectedUser ?? null;
    this.activityToUpgrade = this.dialogConfig.data?.activityToUpgrade ?? null;

    this.initializeForm();

    if (!this.selectedUser) {
      this.consultationForm.get('participantId')?.disable();
      this.disableParticipantSelect = true;
    }
    await Promise.all([this.loadParticipantsAsync(), this.loadCoursesAsync()]);

    this.setForm();

    const courseId = this.consultationForm.get('courseId')?.value;
    if (courseId) {
      this.onCourseSelected(courseId);
    }

    if (this.isUpdate) {
      this.ensureStoredParticipantsSelectable();
    }
  }

  //#region Helper functions

  get isUpdate() {
    return (
      this.activityToUpgrade !== null && this.activityToUpgrade != undefined
    );
  }

  findCourseByName(name: string): number {
    const course = this.availableCourses.find((c) => c.courseName === name);
    if (course === undefined || course === null) {
      return null;
    }
    return course.courseId;
  }

  consultationDialogSubmit(): void {
    const selectedParticipants =
      this.consultationForm.getRawValue().participantId;

    //console.log(this.consultationForm.getRawValue());

    if (
      !this.selectedUser &&
      (!selectedParticipants || selectedParticipants.length === 0)
    ) {
      this.notificationService.warn(
        'Bitte wählen Sie mindestens einen Teilnehmer aus.',
      );
      return;
    }

    this.isSubmitting = true;
    this.fileDropComponent.requestUpload();
  }

  closeDialog(result?: DialogResult) {
    this.consultationForm.reset();
    this.selectedUser = null;
    this.dialogManager.closeConsultationDialog(result);
  }

  //#endregion

  //#region Data loading

  enableUserDropdown(): void {
    this.usersLoading = false;
    this.consultationForm.controls['participantId'].disable();
  }

  enableCourseDropdown(): void {
    this.coursesLoading = false;
    this.consultationForm.controls['courseId'].enable();
  }

  async loadParticipantsAsync(): Promise<void> {
    const users = await firstValueFrom(
      this.userService
        .getUsersObservable()
        .pipe(filter((u) => u && u.length > 0)),
    );

    this.allParticipants = users
      .filter((user) => user.roles.some((r) => r.roleId === 1))
      .sort((a, b) => a.lastname.localeCompare(b.lastname));

    this.enableUserDropdown();
  }

  async loadCoursesAsync(): Promise<void> {
    const userId = this.authService.getCurrentUserId();
    const roleId = this.authService.getUserRole();

    const course$ =
      roleId === 4
        ? this.courseService.getCourseObservable()
        : this.userService.getUserCoursesObservable(userId);

    const courses = await firstValueFrom(
      course$.pipe(filter((c) => c && c.length > 0)),
    );

    this.availableCourses = courses.sort((a, b) =>
      a.courseName.localeCompare(b.courseName),
    );

    const interessentCourse: Course = {
      courseId: -1,
      courseName: '--- Interessent:innen (ohne Kurs) ---',
      users: this.allParticipants.filter((u) => !u.courses?.length),
      startDate: '',
      locationId: 0,
      courseTypeId: 0,
      isActive: true,
    };
    this.availableCourses = [interessentCourse, ...this.availableCourses];

    this.enableCourseDropdown();

    if (this.selectedUser) {
      const userCourse = courses.find((c) =>
        c.users.some((u) => u.userId === this.selectedUser.userId),
      );

      if (userCourse) {
        this.consultationForm.patchValue({ courseId: userCourse.courseId });
      }
    }
  }

  //#endregion

  initializeForm(): void {
    const date = formatDate(new Date(), 'dd.MM.yyyy', 'de-DE');

    this.consultationForm = this.fb.group({
      title: [`Beratungsgespräch - ${date}`, Validators.required],
      courseId: [null, this.selectedUser ? [] : [Validators.required]],
      participantId: this.fb.control(
        this.selectedUser ? [this.selectedUser.userId] : [],
        this.selectedUser ? [] : [Validators.required],
      ),

      remarks: [''],
      status: ['NEU ERSTELLT'],
    });

    this.disableParticipantSelect = !this.selectedUser;
  }

  // Übernimmt Titel, Kurs, Teilnehmer, Bemerkungen und Anhänge aus der zu
  // bearbeitenden Aktivität. Beim Anlegen gibt es nichts vorzubefüllen:
  // initializeForm() und loadCoursesAsync() setzen dort bereits alles Nötige.
  setForm(): void {
    if (!this.isUpdate) return;

    const activity = this.activityToUpgrade;
    this.oldFiles = activity.consultation?.fileUrls ?? [];

    // Ohne Kurs wurde die Beratung über den Pseudo-Kurs "Interessent:innen" angelegt.
    // Dessen Id -1 verwirft das Backend, im Formular muss sie aber wieder stehen.
    const courseName = activity.courseNames?.[0];
    const courseId = courseName ? this.findCourseByName(courseName) : -1;

    // Das Multiselect erwartet ein Array von User-Ids
    const participantIds = (activity.users ?? [])
      .map((u) => u.userId)
      .filter((id) => id != null);

    this.consultationForm.setValue({
      title: activity.activityName,
      participantId: participantIds,
      courseId: courseId,
      remarks: activity.description,
      status: activity.status,
    });
  }

  // Steht ein gespeicherter Teilnehmer nicht mehr in der Kursliste (Lead inzwischen einem
  // Kurs zugeordnet, TN aus dem Kurs entfernt), zeigt das Multiselect sonst trotz
  // korrektem Wert keinen Eintrag an.
  private ensureStoredParticipantsSelectable(): void {
    const stored = this.activityToUpgrade.users ?? [];
    const missing = stored.filter(
      (u) => !this.availableParticipants.some((p) => p.userId === u.userId),
    );
    if (missing.length === 0) return;

    this.availableParticipants = [...missing, ...this.availableParticipants];
    this.disableParticipantSelect = false;
    this.consultationForm.get('participantId')?.enable();
  }

  openConsultationForUser(user: user) {
    if (this.isDialogOpen) return;

    this.isDialogOpen = true;

    const ref = this.dialogManager['dialogService'].open(
      CreateConsultationDialogComponent,
      {
        header: 'Beratungsgespräch',
        baseZIndex: 10000,
        draggable: true,
        data: {
          selectedUser: user,
        },
      },
    );

    ref.onClose.subscribe(() => {
      this.isDialogOpen = false;
    });
  }
  onCourseSelected(courseId: number | null) {
    const participantControl = this.consultationForm.get('participantId');
    if (!this.consultationForm) return;
    if (!courseId) {
      this.availableParticipants = [];
      this.disableParticipantSelect = true;
      participantControl?.setValue([]);
      participantControl?.disable();
      return;
    }

    if (courseId === -1) {
      this.availableParticipants = this.allParticipants.filter(
        (u) => u.isLead && (!u.courses || u.courses.length === 0),
      );
    } else {
      const selectedCourse = this.availableCourses.find(
        (c) => c.courseId === courseId,
      );
      this.availableParticipants = selectedCourse
        ? selectedCourse.users.filter((u) => u.roleIds.includes(1))
        : [];
    }
    if (this.availableParticipants.length === 0) {
      this.disableParticipantSelect = true;
      participantControl?.disable();
    } else {
      this.disableParticipantSelect = false;
      participantControl?.enable();
    }
  }

  //#region File upload

  onFileUploadStarted() {
    this.isSubmitting = true;
  }

  onFilesUploaded(fileUrls: string[]) {
    if (!this.isSubmitting) return;
    this.isSubmitting = false;

    const courseId = this.consultationForm.value.courseId;

    let participantRaw = this.consultationForm.getRawValue().participantId;
    if (this.selectedUser) {
      participantRaw = [this.selectedUser.userId];
    }

    const userIds: number[] = Array.isArray(participantRaw)
      ? participantRaw
          .map((u: any) =>
            typeof u === 'object' && u.userId != null
              ? Number(u.userId)
              : typeof u === 'number'
                ? u
                : NaN,
          )
          .filter((id: number) => !isNaN(id))
      : participantRaw != null
        ? [Number(participantRaw)]
        : [];

    if (userIds.length === 0) {
      this.notificationService.error(
        'Sie müssen mindestens einen Teilnehmer auswählen.',
      );
      this.isSubmitting = false;
      return;
    }
    const formValues: Consultation = {
      courseIds: courseId == null ? [] : [courseId],

      userIds: userIds,
      remarks: this.consultationForm.value.remarks,
      title: this.consultationForm.value.title,
      status: this.consultationForm.value.status,
      fileUrls: fileUrls,
    };

    if (this.isUpdate) {
      this.updateConsultation(formValues);
    } else {
      this.createConsultation(formValues);
    }
  }

  //#endregion

  //#region Create Cons.

  createConsultation(formValues: Consultation) {
    formValues.creatorId = this.authService.getCurrentUserId();
    // console.log('creatorId:', formValues.creatorId);
    this.consultationService.saveConsultation(formValues).subscribe({
      next: (activity) => {
        this.activityService.addActivity(activity);

        this.closeDialog({ success: true, activityId: activity.activityId });

        this.notificationService.success(
          `Aktivität "Beratung" erfolgreich erstellt`,
        );
      },
      error: () => {
        this.notificationService.error(`Aktivitat "Beratung" NICHT erstellt`);
      },
    });

    this.isSubmitting = false;
  }

  //#endregion

  //#region Update cons.

  updateConsultation(formValues: Consultation) {
    formValues.consultationId =
      this.activityToUpgrade.consultation.consultationId;
    formValues.updator = this.authService.getUserName();
    formValues.activityId = this.activityToUpgrade.activityId;

    this.consultationService.updateConsultation(formValues).subscribe({
      next: (activity) => {
        this.closeDialog({ success: true });
        this.activityService.updateActivity(activity);
        this.notificationService.success(
          `Aktivität "Beratung" erfolgreich aktualisiert`,
          activity,
        );
      },
      error: () => {
        this.notificationService.error(
          `Aktivitat "Beratung" NICHT aktualisiert`,
        );
      },
    });

    this.isSubmitting = false;
  }
}
